/**
 * Typed CSS property helpers for React style objects with custom properties.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type React from "react";

export type CssCustomPropertyName = `--${string}`;
export type CssCustomPropertyValue = string | number | undefined;
export type CssCustomProperties = React.CSSProperties &
  Partial<Record<CssCustomPropertyName, CssCustomPropertyValue>>;

export function cssProperties(
  ...styles: readonly (CssCustomProperties | React.CSSProperties | undefined)[]
): CssCustomProperties {
  const merged: CssCustomProperties = {};
  for (const style of styles) {
    if (style !== undefined) Object.assign(merged, style);
  }
  return merged;
}

export function cssVarMap(
  vars: Readonly<Record<CssCustomPropertyName, CssCustomPropertyValue>>,
): CssCustomProperties {
  return vars;
}

export function cssPercent(value: number): string {
  return `${String(value)}%`;
}

export function cssPercentPosition(left: number, top: number): React.CSSProperties {
  return { left: cssPercent(left), top: cssPercent(top) };
}

export function cssPercentSize(
  size: Readonly<{
    height?: number;
    width?: number;
  }>,
): React.CSSProperties {
  return cssProperties(
    size.height === undefined ? undefined : { height: cssPercent(size.height) },
    size.width === undefined ? undefined : { width: cssPercent(size.width) },
  );
}
